/**
 * Read-aloud controls backed by the browser-native Web Speech API.
 *
 * `SpeechSynthesis` is supported on every tablet/desktop browser a child is
 * likely to use and runs entirely on-device, so read-aloud works offline with
 * no extra cached assets. Word-boundary highlighting is best-effort: Chromium
 * and Safari fire `boundary` events; Firefox does not, so it simply reads
 * without per-word highlight.
 */

import { useCallback, useEffect, useRef, useState } from "react";

interface ReadAloudProps {
  /** The page text to read. */
  text: string;
  /** Called with the character offset of the current word for highlighting. */
  onBoundary?: (charIndex: number) => void;
}

type Playback = "idle" | "speaking" | "paused";

/** Key under which the child's chosen voice is remembered across pages and books. */
const VOICE_STORAGE_KEY = "creative-reading.readAloudVoiceURI";

export function ReadAloud({ text, onBoundary }: ReadAloudProps) {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const [playback, setPlayback] = useState<Playback>("idle");
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceURI, setVoiceURI] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(VOICE_STORAGE_KEY) ?? "";
  });
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Remember the chosen voice so it carries over to other pages and books.
  const selectVoice = useCallback((nextVoiceURI: string) => {
    setVoiceURI(nextVoiceURI);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VOICE_STORAGE_KEY, nextVoiceURI);
    }
  }, []);

  // Load the available English voices so a child can switch from the default.
  useEffect(() => {
    if (!supported) return;
    const loadVoices = () => {
      const all = window.speechSynthesis.getVoices();
      setVoices(all.filter((voice) => voice.lang.startsWith("en")));
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
    };
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setPlayback("idle");
  }, [supported]);

  // Stop reading if the page text changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
    };
  }, [supported, text]);

  const speak = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.05;
    utterance.lang = "en-US";
    const selectedVoice = voices.find((voice) => voice.voiceURI === voiceURI);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.onboundary = (event) => {
      if (event.name === "word" || event.charIndex >= 0) {
        onBoundary?.(event.charIndex);
      }
    };
    utterance.onend = () => setPlayback("idle");
    utterance.onerror = () => setPlayback("idle");
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setPlayback("speaking");
  }, [onBoundary, supported, text, voices, voiceURI]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setPlayback("paused");
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setPlayback("speaking");
  }, [supported]);

  if (!supported) {
    return (
      <p role="note" style={{ fontSize: "1rem", color: "var(--color-slate)" }}>
        Read-aloud is not available on this device.
      </p>
    );
  }

  return (
    <div
      role="group"
      aria-label="Read aloud controls"
      style={{
        display: "flex",
        gap: "0.75rem",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {voices.length > 0 && (
        <label
          style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
        >
          <span style={{ fontSize: "1rem" }}>🗣️ Voice</span>
          <select
            className="kid-select"
            aria-label="Choose a reading voice"
            value={voiceURI}
            onChange={(event) => selectVoice(event.target.value)}
          >
            <option value="">Default voice</option>
            {voices.map((voice) => (
              <option key={voice.voiceURI} value={voice.voiceURI}>
                {voice.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {playback === "idle" && (
        <button
          type="button"
          className="kid-button kid-button--secondary"
          onClick={speak}
        >
          🔊 Read to me
        </button>
      )}
      {playback === "speaking" && (
        <button
          type="button"
          className="kid-button kid-button--secondary"
          onClick={pause}
        >
          ⏸ Pause
        </button>
      )}
      {playback === "paused" && (
        <button
          type="button"
          className="kid-button kid-button--secondary"
          onClick={resume}
        >
          ▶ Keep reading
        </button>
      )}
      {playback !== "idle" && (
        <button type="button" className="kid-button" onClick={stop}>
          ⏹ Stop
        </button>
      )}
    </div>
  );
}
